import CrudPage from '@modules/cruds/CrudPage';
import ViewForm from './ViewForm';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { postOrganizationDefaultView, postUserDefaultView, putUserDefaultView } from '@services/ApiService';
import { requestActions } from '@redux/slices/functionals/request/requestSlice';
import { defaultConfigComponent } from './Views';
import { CViewType } from '@ts/ui-models';
import { globalActions } from '@redux/slices/states/global/globalSlice';

const viewSchema = yup.object().shape({
  name: yup.string().required().label('Name'),
  viewTypeId: yup.string().required().label('View type'),
});

const ViewPage = () => {
  const dispatch = useAppDispatch();
  const settings = useAppSelector(state => state.global.settings);
  const badge = useAppSelector(state => state.global.sessionBadge);

  const defaultValues = {
    properties: {
      to: new Date(),
      adjustCenterZoom: true,
    },
    defaultView: !Boolean(settings?.defaultView?.defaultViewId),
    viewTypeId: 'map2',
    viewType: new CViewType({
      id: 'map2',
      name: 'Map',
      logicalId: 'map2',
      coordinates: 'GPS',
    }),
    size: [null, null],
    xViewLayers: [],
  };

  /**
   * Performs post-submission actions for setting a default view configuration,
   * supporting both organization-level and user-level preferences.
   */
  const extraActionToDoAfterCorrectSubmit = async (
    values: { defaultView?: boolean },
    options: { itemId: string }
  ): Promise<void> => {
    if (!badge) return;

    // If user didn't toggle the defaultView option and settings already exist, do nothing
    if (values.defaultView === undefined && settings?.defaultView) {
      return;
    }

    const defaultViewEnabled = values.defaultView === true;
    const { level = 'user', parameterId } = settings?.defaultView || {};

    /**
     * Handles updating the Redux store after a successful API call
     */
    const handleUpdateSettings = (response: any) => {
      const defaultViewId = response?.payload?.data?.value?.defaultViewId;
      if (!defaultViewId) return;

      dispatch(
        globalActions.updateGlobalState({
          key: 'settings',
          value: {
            ...settings,
            defaultView: {
              ...settings?.defaultView,
              defaultViewId,
            },
          },
        })
      );

      // Clean up request state
      dispatch(requestActions.cleanKey('defaultView'));
    };

    /**
     * CASE 1:
     * - User wants to set a default view.
     * - No existing setting OR existing setting is at organization level.
     * => Create new user-level default view.
     */
    if (defaultViewEnabled && (level === 'organization' || !settings?.defaultView)) {
      await postUserDefaultView(dispatch, badge.userId, badge.organizationId, options.itemId).then(handleUpdateSettings);

      /**
       * CASE 2:
       * - Existing setting is at user level
       * => Either update or delete user-level default view.
       */
    } else if (level === 'user') {
      if (defaultViewEnabled) {
        await putUserDefaultView(dispatch, parameterId, options.itemId).then(handleUpdateSettings);
      } else {
        // Optional: Implement deletion if required in the future
        // await deleteUserDefaultView(dispatch, badge.userId, parameterId)
        //   .then(handleUpdateSettings);
      }

      /**
       * CASE 3:
       * - Fallback: Set an organization-level default view
       */
    } else {
      await postOrganizationDefaultView(dispatch, badge.organizationId, options.itemId).then(handleUpdateSettings);
    }
  };
  //#endregion

  return (
    <CrudPage
      key={'viewPage'}
      configComponent={defaultConfigComponent}
      form={ViewForm}
      defaultValues={defaultValues}
      validationSchema={viewSchema}
      extraActionToDoAfterCorrectSubmit={extraActionToDoAfterCorrectSubmit}
    />
  );
};

export default ViewPage;
